import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface PhpCategory {
  id: number;
  code: string;
  name: string;
  subtitle: string;
  icon: string;
  colour: string;
  sort_order: number;
  offered: boolean;
  asks_tenant: boolean;
  asks_taken: boolean;
  asks_action: boolean;
  asks_response: boolean;
  tracks_violation: boolean;
  events: string[];
  outcomes: string[];
  event_outcomes: Record<string, string[]>;
  questions: string[];
  /** The property types it belongs to (one key); [] = every type. */
  site_types?: string[];
  asks: string[];
  reports: number;
}

export interface ClientNotificationFootage {
  type: 'image' | 'video';
  url: string;
}

/** One alert's photos and videos offered in the Client Notification popup (display only). */
export interface ClientNotificationFootageGroup {
  alertId: string;
  /** Camera and time, shown as the caption. */
  label: string;
  /** The alert the operator has open; its media starts ticked. */
  isCurrent: boolean;
  /** The alert's media is still being fetched. */
  loading?: boolean;
  items: ClientNotificationFootage[];
}

export interface IncidentFormOption {
  code: string;
  name: string;
}

/** One category's report form on one property, as the portal describes it. */
export interface ClientIncidentFormDef {
  date: string;
  time: string;
  areas: IncidentFormOption[];
  events: IncidentFormOption[];
  /** The results each "what happened" answer offers, by answer code. */
  results: Record<string, IncidentFormOption[]>;
  /** "How he gained access": asked only for the answers in `for`. */
  how: { for: string[]; options: IncidentFormOption[] } | null;
  tenant_assoc: IncidentFormOption[] | null;
  /** The tenant answers that need an apartment named. */
  tenant_units_when: string[];
  tenant_taken: IncidentFormOption[] | null;
  response: IncidentFormOption[] | null;
  actions: { options: IncidentFormOption[]; none: string; other: string } | null;
  questions: { code: string; label: string; answers: string[] }[];
}

export interface ClientIncidentProperty {
  id: number;
  name: string;
  client: string;
  /** Property type key (residential / construction / storage / commercial); '' when not set. */
  type?: string;
  /** The property type in words, e.g. "Construction site". */
  type_label?: string;
}

/**
 * What the portal knows about a site, before any category is picked: the property it is on (with
 * its type) and the categories that type files under. A 404 means the site is mapped to no property.
 */
export interface ClientIncidentContext {
  ok: boolean;
  property: ClientIncidentProperty;
  categories: PhpCategory[];
  icons: Record<string, string>;
}

/**
 * Either the form (`property` and `form`), or — when the site is on several properties and none was
 * named — `needs_property` with the properties to choose from: each property ticks its own areas, so
 * the form cannot be drawn until one is picked.
 */
export interface ClientIncidentFormResponse {
  ok: boolean;
  category: { id: number; code: string; name: string };
  property?: ClientIncidentProperty;
  form?: ClientIncidentFormDef;
  needs_property?: boolean;
  properties?: ClientIncidentProperty[];
}

/** What the operator filled in, as the portal's save expects it. */
export interface ClientIncidentSubmission {
  site_name: string;
  category_id: number;
  /** Needed when the site is on more than one property. */
  property_id?: number | null;
  footage: ClientNotificationFootage[];
  date: string;
  time: string;
  area: string;
  type: string;
  how: string;
  tenant_assoc: string;
  tenant_taken: string;
  response: string;
  actions: string[];
  action_other: string;
  answers: Record<string, string>;
  tenant_units: { no: string; name: string }[];
  result: string;
  body: string;
}

export interface ClientNotificationResult {
  ok: boolean;
  /** What happened, in words: which property the report went on, and whether it is live. */
  message?: string;
  error?: string;
  event_id?: number;
  /** 'live' (footage attached, shows in the client portal) or 'held' (no footage yet). */
  status?: 'live' | 'held';
  duplicate?: boolean;
}

export interface PhpCatalogOption { code: string; name: string; offered?: boolean; }

export interface PhpCategoryCatalog {
  events: PhpCatalogOption[];
  outcomes: PhpCatalogOption[];
  questions: { code: string; label: string; answers: string[] }[];
  hows: PhpCatalogOption[];
  actions: PhpCatalogOption[];
  responses: PhpCatalogOption[];
  icons: { key: string; svg: string }[];
  colours: { hex: string; name: string }[];
}

export interface PhpCategoryList {
  ok: boolean;
  categories: PhpCategory[];
  /** Icon key -> its drawing, for the cards. */
  icons: Record<string, string>;
}

export interface PhpCategoryDetail {
  ok: boolean;
  category: PhpCategory;
  catalog: PhpCategoryCatalog;
  message?: string;
}

/**
 * The client portal's incident categories, over its api/categories.php (SightWatch-New-UI).
 *
 * Plain HttpClient on purpose, not CommonService: that one adds this platform's admin headers, and
 * those must never be sent to another service. The portal's API takes no sign-in yet.
 */
@Injectable({ providedIn: 'root' })
export class PhpCategoryService {
  private http = inject(HttpClient);

  /** False when no portal address is configured; the section then stays hidden. */
  get enabled(): boolean {
    return !!environment.phpPortalUrl;
  }

  private get url(): string {
    return `${environment.phpPortalUrl.replace(/\/+$/, '')}/api/categories.php`;
  }

  list(): Observable<PhpCategoryList> {
    return this.http.get<PhpCategoryList>(this.url);
  }

  get(id: number): Observable<PhpCategoryDetail> {
    return this.http.get<PhpCategoryDetail>(this.url, { params: { id } });
  }

  save(id: number, body: Record<string, unknown>): Observable<PhpCategoryDetail> {
    return this.http.post<PhpCategoryDetail>(this.url, body, { params: { id } });
  }

  /**
   * Deletes a category AND every report filed under it. `reports` is the number the person was
   * shown; the portal refuses (409) when the real count has changed, so nothing is deleted that
   * they did not agree to.
   */
  delete(id: number, reports: number): Observable<{ ok: boolean; message?: string; deleted_reports?: number }> {
    return this.http.delete<{ ok: boolean; message?: string; deleted_reports?: number }>(this.url, {
      params: { id, confirm_reports: reports },
    });
  }

  /** A blank category (id 0) and the options it can choose from, to fill in a new one. */
  getNew(): Observable<PhpCategoryDetail> {
    return this.http.get<PhpCategoryDetail>(this.url, { params: { new: 1 } });
  }

  /** Adds a category in the portal from a filled-in form shaped like the edit's. */
  create(body: Record<string, unknown>): Observable<PhpCategoryDetail> {
    return this.http.post<PhpCategoryDetail>(this.url, body);
  }

  private get notificationUrl(): string {
    return `${environment.phpPortalUrl.replace(/\/+$/, '')}/api/client-notification.php`;
  }

  /**
   * The portal's form for one category, drawn for the property the site belongs to. Doubles as the
   * site check: a site that is on no property (or several) comes back as an error with a message.
   */
  getClientIncidentForm(siteName: string, categoryId: number, propertyId?: number): Observable<ClientIncidentFormResponse> {
    const params: Record<string, string | number> = { site_name: siteName, category_id: categoryId };
    if (propertyId) {
      params['property_id'] = propertyId;
    }
    return this.http.get<ClientIncidentFormResponse>(this.notificationUrl, { params });
  }

  /**
   * The site check plus the property and the categories that suit its type. Used by the report
   * popup to decide whether to show the portal's form at all: an error means the site is not mapped.
   */
  getClientIncidentContext(siteName: string, propertyId?: number): Observable<ClientIncidentContext> {
    const params: Record<string, string | number> = { site_name: siteName };
    if (propertyId) {
      params['property_id'] = propertyId;
    }
    return this.http.get<ClientIncidentContext>(this.notificationUrl, { params });
  }

  /** Files the filled-in form as an incident report in the portal. */
  submitClientIncident(body: ClientIncidentSubmission): Observable<ClientNotificationResult> {
    return this.http.post<ClientNotificationResult>(this.notificationUrl, body);
  }
}
